// ==========================================================================
// TelegramAuthGate — Mini-App auth path. Loads the token from Telegram
// CloudStorage (fallback localStorage), exchanges initData for a Sanctum
// token if needed, and gates the same routes PhoneAuthGate gates.
// ==========================================================================

import { backgroundImage, weatherIcon } from '@/assets';
import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useUser } from '@/hooks/useUser';
import { cloudGet, cloudRemove, cloudSet, getInitData } from '@/lib/telegram';
import { verifyInitData } from '@/services/telegram-auth.service';
import { LoadingScreen } from './PhoneAuthGate';
import { getTelegramRequiredRoute } from './authRouting';

interface TelegramAuthGateProps {
  children: React.ReactNode;
}

const ACCESS_TOKEN_KEY = 'access_token';

type TgPhase =
  | { kind: 'loading' }
  | { kind: 'authenticated' }
  | { kind: 'fatal'; reason: string };

export function TelegramAuthGate({ children }: TelegramAuthGateProps) {
  const location = useLocation();
  const [phase, setPhase] = useState<TgPhase>({ kind: 'loading' });

  // Two-step bootstrap:
  //   1. Restore the token from CloudStorage / localStorage.
  //   2. If a token is present, useUser fetches the profile.
  //   3. If no token (or step-1 said cleared after a 401), call
  //      verifyInitData to mint a fresh one.
  //
  // Depends on phase.kind so the 401 handler can re-trigger us by
  // setting phase back to 'loading'. The guard at the top is what
  // stops infinite loops once we transition out of loading.
  useEffect(() => {
    if (phase.kind !== 'loading') return;
    let cancelled = false;

    (async () => {
      const cached = await cloudGet(ACCESS_TOKEN_KEY);
      if (cancelled) return;

      if (cached) {
        // Mirror to localStorage so axios interceptor + useUser's
        // enabled-when-token check find it.
        localStorage.setItem(ACCESS_TOKEN_KEY, cached);
        setPhase({ kind: 'authenticated' });
        return;
      }

      const initData = getInitData();
      if (!initData) {
        setPhase({ kind: 'fatal', reason: 'no-init-data' });
        return;
      }

      try {
        const { access_token } = await verifyInitData({ init_data: initData });
        if (cancelled) return;
        await cloudSet(ACCESS_TOKEN_KEY, access_token);
        localStorage.setItem(ACCESS_TOKEN_KEY, access_token);
        setPhase({ kind: 'authenticated' });
      } catch (err) {
        // Surface enough detail to diagnose from the Telegram webview's
        // devtools without exposing it on the user-facing screen.
        // eslint-disable-next-line no-console
        console.error('[TelegramAuthGate] verifyInitData failed', err);

        const reason = extractVerifyFailureReason(err);
        if (!cancelled) {
          setPhase({ kind: 'fatal', reason });
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [phase.kind]);

  const tokenPresent = !!localStorage.getItem(ACCESS_TOKEN_KEY);
  const { data: user, isLoading, isError, error } = useUser(tokenPresent && phase.kind === 'authenticated');

  // 401 from /user/me means our cached token was killed server-side.
  // Drop it and re-trigger the verifyInitData path. We MUST await
  // cloudRemove before flipping phase back to 'loading', otherwise the
  // bootstrap effect can race in and read the stale token before
  // CloudStorage has finished clearing it.
  useEffect(() => {
    if (!isError) return;
    const status = (error as { response?: { status?: number } })?.response?.status;
    if (status !== 401) return;

    localStorage.removeItem(ACCESS_TOKEN_KEY);
    let cancelled = false;
    void cloudRemove(ACCESS_TOKEN_KEY).then(() => {
      if (!cancelled) {
        setPhase({ kind: 'loading' });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [isError, error]);

  if (phase.kind === 'fatal') {
    return <FatalScreen reason={phase.reason} />;
  }

  if (phase.kind === 'loading' || (tokenPresent && isLoading)) {
    return <LoadingScreen />;
  }

  if (user) {
    const requiredRoute = getTelegramRequiredRoute(user, location.pathname);
    if (requiredRoute) {
      return <Navigate to={requiredRoute} replace />;
    }
  }

  return <>{children}</>;
}

/**
 * Pull a short reason code out of an axios / fetch error so the fatal
 * screen tells us what kind of failure we hit. Format: "verify-failed-401"
 * for HTTP errors, "verify-failed-network" when there was no response at
 * all, plain "verify-failed" otherwise.
 */
function extractVerifyFailureReason(err: unknown): string {
  const e = err as { response?: { status?: number; data?: { reason?: string } }; message?: string };
  if (e?.response?.status) {
    const apiReason = e.response.data?.reason;
    return apiReason
      ? `verify-failed-${e.response.status}-${apiReason}`
      : `verify-failed-${e.response.status}`;
  }
  if (e?.message) {
    return 'verify-failed-network';
  }
  return 'verify-failed';
}

/**
 * Terminal failure UI. We don't show an OTP fallback here — the user is
 * inside the Telegram webview and can close + reopen the Mini-App to get
 * fresh initData, which is the only thing that recovers from this state.
 */
function FatalScreen({ reason }: { reason: string }) {
  const { t } = useTranslation();
  return (
    <div
      className="min-h-screen flex flex-col items-center justify-center bg-cover bg-center bg-no-repeat px-6 text-center"
      style={{ backgroundImage: `url(${backgroundImage})` }}
    >
      <img src={weatherIcon} alt="" className="w-[120px] h-[120px] object-contain mb-6" />
      <h1 className="text-lg font-semibold text-gray-900">
        {t('telegram.auth.fatalTitle', 'Please reopen from Telegram')}
      </h1>
      <p className="mt-2 text-sm text-gray-700 max-w-xs">
        {t(
          'telegram.auth.fatalBody',
          'Close this window and tap the Yaya menu button in the bot chat to start fresh.'
        )}
      </p>
      <p className="mt-4 text-xs text-gray-500">code: {reason}</p>
    </div>
  );
}
